// Import React & core libraries
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

const COOKIE_NAME = 'halisi_cookie_consent'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 // 1 year

const DEFAULT_PREFERENCES = {
  essential: true, // always on, cannot be disabled
  analytics: true,
  personalization: true,
  marketing: true,
}

// ── Storage helpers ──
// Cookies are the primary store (so consent travels with the request the
// way a real "cookie" is supposed to). But `document.cookie` writes are
// silently rejected in a lot of common dev situations -- a sandboxed
// preview iframe (CodeSandbox/StackBlitz-style embeds), a cross-site
// third-party context, or the page being opened from file:// instead of
// http(s)://. When that happens we fall back to localStorage so consent
// still persists instead of the banner reappearing on every reload.

function writeCookie(payload) {
  try {
    const value = encodeURIComponent(JSON.stringify(payload))
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
    // Verify the browser actually accepted it -- some contexts fail silently.
    return readCookie() !== null
  } catch (err) {
    return false
  }
}

function readCookie() {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'),
    )
    if (!match) return null
    return JSON.parse(decodeURIComponent(match[1]))
  } catch (err) {
    return null
  }
}

function writeLocalStorage(payload) {
  try {
    window.localStorage.setItem(COOKIE_NAME, JSON.stringify(payload))
    return true
  } catch (err) {
    return false
  }
}

function readLocalStorage() {
  try {
    const raw = window.localStorage.getItem(COOKIE_NAME)
    return raw ? JSON.parse(raw) : null
  } catch (err) {
    return null
  }
}

export function setConsentCookie(preferences) {
  const payload = {
    ...preferences,
    essential: true,
    consentedAt: new Date().toISOString(),
  }

  const cookieOk = writeCookie(payload)
  if (!cookieOk) {
    // eslint-disable-next-line no-console
    console.warn(
      '[CookieConsent] document.cookie write failed or was blocked ' +
        '(common in sandboxed preview iframes, file:// pages, or ' +
        'third-party embeds) -- falling back to localStorage so the ' +
        'choice still persists.',
    )
    writeLocalStorage(payload)
  } else {
    // Keep localStorage in sync too, so the fallback path also has the
    // latest value if cookies get blocked later.
    writeLocalStorage(payload)
  }

  return payload
}

export function getConsentCookie() {
  return readCookie() || readLocalStorage()
}

export function clearConsentCookie() {
  try {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`
  } catch (err) {
    // ignore
  }
  try {
    window.localStorage.removeItem(COOKIE_NAME)
  } catch (err) {
    // ignore
  }
}

/**
 * CookieConsent
 *
 * Renders nothing until:
 *  - no consent record exists yet (first visit -> shows the banner), or
 *  - a parent calls ref.current.openPreferences() (e.g. a "Change cookie
 *    settings" footer link).
 *
 * Usage:
 *   const cookieRef = useRef(null)
 *   <CookieConsent ref={cookieRef} onConsentChange={(prefs) => {...}} />
 *   <button onClick={() => cookieRef.current.openPreferences()}>Change cookie settings</button>
 */
const CookieConsent = forwardRef(({ onConsentChange }, ref) => {
  const [view, setView] = useState('hidden') // 'hidden' | 'banner' | 'manage'
  const [draftPrefs, setDraftPrefs] = useState(DEFAULT_PREFERENCES)

  useEffect(() => {
    const existing = getConsentCookie()
    // eslint-disable-next-line no-console
    if (existing) {
      onConsentChange?.(existing)
    } else {
      setView('banner')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Dev convenience: run window.__clearHalisiConsent() in the console to
  // wipe stored consent and reload, without having to dig through
  // DevTools > Application every time.
  useEffect(() => {
    window.__clearHalisiConsent = () => {
      clearConsentCookie()
      window.location.reload()
    }
    return () => {
      delete window.__clearHalisiConsent
    }
  }, [])

  useImperativeHandle(ref, () => ({
    openPreferences: () => {
      const existing = getConsentCookie()
      setDraftPrefs(existing || DEFAULT_PREFERENCES)
      setView('manage')
    },
  }))

  // Lock the page's own scroll while the panel is open. Without this, the
  // page (behind the fixed overlay) and the overlay itself are two
  // independent scroll containers, so if the panel content is tall you
  // get two separate scrollbars fighting each other. Locking body scroll
  // means only the overlay scrolls, matching normal modal behavior.
  useEffect(() => {
    if (view === 'hidden') return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [view])

  const applyAndClose = (preferences) => {
    const saved = setConsentCookie(preferences)
    onConsentChange?.(saved)
    setView('hidden')
  }

  const handleAcceptAll = () => {
    applyAndClose({ analytics: true, personalization: true, marketing: true })
  }

  const handleRejectAll = () => {
    applyAndClose({
      analytics: false,
      personalization: false,
      marketing: false,
    })
  }

  const openManage = () => {
    const existing = getConsentCookie()
    setDraftPrefs(existing || DEFAULT_PREFERENCES)
    setView('manage')
  }

  const handleSaveChoices = () => {
    applyAndClose(draftPrefs)
  }

  const handleCancel = () => {
    // Cancel closes the manage panel; if there's still no record at all,
    // fall back to the banner rather than leaving the user with nothing.
    setView(getConsentCookie() ? 'hidden' : 'banner')
  }

  const toggle = (key) => {
    if (key === 'essential') return // always on
    setDraftPrefs((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  if (view === 'hidden') return null

  return (
    <>
      <style>{cookieStyles}</style>
      <div className='halisi-cookie-backdrop' role='presentation'>
        {view === 'banner' && (
          <div
            className='halisi-cookie-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='cookie-consent-title'
          >
            <h2 id='cookie-consent-title' className='halisi-cookie-title'>
              Cookies &amp; Privacy
            </h2>
            <p className='halisi-cookie-body'>
              This website uses essential and optional cookies to ensure
              functionality, improve performance, and personalize your
              experience. You can accept all cookies, refuse them, or customize
              your choices. You may change your preferences at any time.
            </p>
            <div className='halisi-cookie-actions'>
              <button
                type='button'
                className='halisi-cookie-btn halisi-cookie-btn--primary'
                onClick={handleAcceptAll}
              >
                Accept all cookies
              </button>
              <button
                type='button'
                className='halisi-cookie-btn halisi-cookie-btn--ghost'
                onClick={handleRejectAll}
              >
                Reject all
              </button>
              <button
                type='button'
                className='halisi-cookie-btn halisi-cookie-btn--ghost'
                onClick={openManage}
              >
                Customize
              </button>
            </div>
          </div>
        )}

        {view === 'manage' && (
          <div
            className='halisi-cookie-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='cookie-manage-title'
          >
            <h2 id='cookie-manage-title' className='halisi-cookie-title'>
              Manage cookie preferences
            </h2>
            <p className='halisi-cookie-body'>
              You can enable or disable optional cookies below. Essential
              cookies are always active.
            </p>

            <div className='halisi-cookie-row'>
              <button
                type='button'
                className='halisi-cookie-toggle halisi-cookie-toggle--locked'
                aria-pressed='true'
                disabled
              >
                <span className='halisi-cookie-toggle__knob' />
              </button>
              <div>
                <p className='halisi-cookie-row__label'>
                  Essential cookies.{' '}
                  <span className='halisi-cookie-row__desc'>
                    Always active. Required for the website to function.
                  </span>
                </p>
              </div>
            </div>

            <div className='halisi-cookie-row'>
              <button
                type='button'
                className={`halisi-cookie-toggle${draftPrefs.analytics ? ' halisi-cookie-toggle--on' : ''}`}
                aria-pressed={draftPrefs.analytics}
                onClick={() => toggle('analytics')}
              >
                <span className='halisi-cookie-toggle__knob' />
              </button>
              <div>
                <p className='halisi-cookie-row__label'>
                  Performance &amp; analytics.{' '}
                  <span className='halisi-cookie-row__desc'>
                    Help us understand how visitors use our site.
                  </span>
                </p>
              </div>
            </div>

            <div className='halisi-cookie-row'>
              <button
                type='button'
                className={`halisi-cookie-toggle${draftPrefs.personalization ? ' halisi-cookie-toggle--on' : ''}`}
                aria-pressed={draftPrefs.personalization}
                onClick={() => toggle('personalization')}
              >
                <span className='halisi-cookie-toggle__knob' />
              </button>
              <div>
                <p className='halisi-cookie-row__label'>
                  Personalization.{' '}
                  <span className='halisi-cookie-row__desc'>
                    Adapt content and recommendations to your profile.
                  </span>
                </p>
              </div>
            </div>

            <div className='halisi-cookie-row'>
              <button
                type='button'
                className={`halisi-cookie-toggle${draftPrefs.marketing ? ' halisi-cookie-toggle--on' : ''}`}
                aria-pressed={draftPrefs.marketing}
                onClick={() => toggle('marketing')}
              >
                <span className='halisi-cookie-toggle__knob' />
              </button>
              <div>
                <p className='halisi-cookie-row__label'>
                  Marketing.{' '}
                  <span className='halisi-cookie-row__desc'>
                    Used to deliver relevant ads and measure effectiveness.
                  </span>
                </p>
              </div>
            </div>

            <div className='halisi-cookie-actions halisi-cookie-actions--manage'>
              <button
                type='button'
                className='halisi-cookie-btn halisi-cookie-btn--primary'
                onClick={handleSaveChoices}
              >
                Save my choices
              </button>
              <button
                type='button'
                className='halisi-cookie-btn halisi-cookie-btn--ghost'
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
})

CookieConsent.displayName = 'CookieConsent'

const cookieStyles = `
  .halisi-cookie-backdrop {
    position: fixed;
    inset: 0;
    /* No page-wide dimming -- the rest of the page stays fully visible.
       This layer is only here to catch clicks outside the panel so the
       banner still behaves modally without visually shadowing the page. */
    background: transparent;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 130px 32px 20px;
    overflow-y: auto;
    z-index: 1000;
    font-family: 'DM Sans', -apple-system, sans-serif;
  }

  @media (max-width: 900px) {
    .halisi-cookie-backdrop {
      padding: 90px 20px 20px;
    }
  }

  @media (max-width: 480px) {
    .halisi-cookie-backdrop {
      padding: 80px 14px 16px;
      align-items: center;
    }
  }

  .halisi-cookie-modal {
    width: 100%;
    max-width: 920px;
    /* No max-height / scroll -- the panel grows to fit its content
       (the manage-preferences view has more rows than the banner). */
    background: rgba(30, 15, 18, 0.9);
    color: #f6e9ec;
    border-radius: 4px;
    padding: 28px 34px 26px;
    /* No shadow -- the panel sits flat on the page, nothing behind it
       is dimmed or shadowed. */
    box-shadow: none;
    pointer-events: auto;
  }

  @media (max-width: 480px) {
    .halisi-cookie-modal {
      padding: 22px 20px 20px;
      border-radius: 4px;
    }
  }

  .halisi-cookie-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 12px;
    color: #ffffff;
  }

  .halisi-cookie-body {
    font-size: 13.5px;
    line-height: 1.65;
    color: #e7d3d8;
    margin: 0 0 20px;
  }

  .halisi-cookie-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .halisi-cookie-actions--manage {
    margin-top: 22px;
  }

  .halisi-cookie-btn {
    border-radius: 4px;
    padding: 10px 18px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: 1.5px solid #d94f85;
    background: transparent;
    color: #f6e9ec;
    transition: background 0.2s, border-color 0.2s, transform 0.15s;
  }

  @media (max-width: 480px) {
    .halisi-cookie-btn {
      flex: 1 1 auto;
      text-align: center;
    }
  }

  .halisi-cookie-btn:hover {
    transform: translateY(-1px);
  }

  .halisi-cookie-btn--primary {
    background: #a8295d;
    border-color: #a8295d;
    color: #ffffff;
  }

  .halisi-cookie-btn--primary:hover {
    background: #8f2050;
  }

  .halisi-cookie-btn--ghost:hover {
    border-color: #f6e9ec;
    background: rgba(246, 233, 236, 0.08);
  }

  .halisi-cookie-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 12px 0;
    border-top: 1px solid rgba(246, 233, 236, 0.12);
  }

  .halisi-cookie-row__label {
    font-size: 13.5px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }

  .halisi-cookie-row__desc {
    font-weight: 400;
    color: #e7d3d8;
  }

  .halisi-cookie-toggle {
    flex-shrink: 0;
    width: 40px;
    height: 22px;
    border-radius: 99px;
    border: none;
    background: rgba(246, 233, 236, 0.25);
    padding: 3px;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-top: 2px;
    transition: background 0.2s;
  }

  .halisi-cookie-toggle--on,
  .halisi-cookie-toggle--locked {
    background: #a8295d;
  }

  .halisi-cookie-toggle--locked {
    cursor: not-allowed;
    opacity: 0.85;
  }

  .halisi-cookie-toggle__knob {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #ffffff;
    transform: translateX(0);
    transition: transform 0.2s;
  }

  .halisi-cookie-toggle--on .halisi-cookie-toggle__knob,
  .halisi-cookie-toggle--locked .halisi-cookie-toggle__knob {
    transform: translateX(18px);
  }
`

export default CookieConsent
